import { useEffect } from "react";

import { useText } from "../i18n.js";
import { DIALOGS } from "../text/app.js";

interface Props {
  readonly src: string;
  readonly alt: string;
  /** A name to save it under; the caption too, when there is no alt. */
  readonly name?: string;
  readonly closing?: boolean;
  readonly onClose: () => void;
}

/**
 * A picture opened large over the page, as a chat app shows one: the dark
 * backdrop, the picture fitted to the window, its caption and a way to save
 * it. Escape, the backdrop or the × close it; nothing leaves the tab.
 */
export function Lightbox({ src, alt, name, closing = false, onClose }: Props): React.JSX.Element {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const t = useText(DIALOGS);
  const caption = alt.length > 0 ? alt : (name ?? "");
  return (
    <div className={`lightbox${closing ? " closing" : ""}`} role="dialog" aria-label={caption.length > 0 ? caption : t.image} onClick={onClose}>
      <div className="lightbox-head" onClick={(event) => event.stopPropagation()}>
        <span className="lightbox-caption" title={caption}>
          {caption}
        </span>
        <a className="lightbox-save" href={src} download={name ?? t.imageFile} title={t.savePicture}>
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5M4 15.5h12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
        <button type="button" className="icon" onClick={onClose} aria-label={t.close} title={t.closeEsc}>
          ×
        </button>
      </div>
      <div className="lightbox-body">
        <img src={src} alt={alt} onClick={(event) => event.stopPropagation()} />
      </div>
    </div>
  );
}
